import { useEffect, useMemo, useState } from "react";

import { cn } from "@/lib/utils";

interface GalleryFrame {
  alt: string;
  src: string;
  badge?: string;
  caption?: string;
}

export interface HeroBeforeAfterGalleryProps {
  before: GalleryFrame;
  after: GalleryFrame[];
  autoAdvanceMs?: number;
  className?: string;
}

const DEFAULT_AUTO_ADVANCE_MS = 3000;

const HeroArrow = ({ className }: { className?: string }) => (
  <svg
    width="48"
    height="218"
    viewBox="0 0 48 218"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={cn("drop-shadow-[0_6px_18px_rgba(249,115,22,0.35)]", className)}
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M43.6953 0.553886C43.2025 1.04659 43.1086 1.32814 43.1086 2.64201C43.1086 3.6978 43.2729 4.54243 43.6484 5.41053C44.1412 6.63055 44.1647 6.84171 44.1412 14.8657C44.1178 23.4294 43.9535 28.0514 42.9913 45.601C41.7006 68.8753 40.668 81.0521 38.7671 95.3404C36.8193 110.075 32.6656 130.745 29.1219 143.555C26.6344 152.447 19.9696 172.765 16.9422 180.601C14.1496 187.828 9.92543 196.72 8.35309 198.691L7.88374 199.277L8.02454 196.227C8.30615 190.197 8.23576 173.727 7.93068 173.187C7.60213 172.53 6.49915 171.944 5.65432 171.944C4.66868 171.944 3.54224 173.234 3.23716 174.759C3.09635 175.463 2.86167 179.264 2.74433 183.206C2.36885 195.101 1.89949 200.849 0.749579 207.723C-0.165658 213.19 -0.21259 214.316 0.444503 215.56C1.2424 217.085 1.99337 217.554 3.87078 217.648C5.11457 217.718 5.84206 217.624 7.27359 217.132C9.69075 216.311 15.5811 213.472 22.6683 209.694C32.3839 204.509 31.9381 204.791 31.3983 204.251C30.8116 203.665 19.2655 206.527 11.1692 209.272C8.89285 210.023 6.96851 210.656 6.87464 210.656C6.80423 210.656 6.73382 210.304 6.73382 209.882C6.73382 209.295 7.08585 208.709 8.28269 207.254C14.9475 199.136 19.5706 189.165 26.6344 167.721C33.4165 147.121 35.599 138.98 39.0018 121.266C44.1412 94.6131 46.136 73.0281 47.1451 33.0957C47.3563 24.4148 47.3563 20.3793 47.1216 15.0065C46.6757 4.16704 46.2768 1.11697 45.1738 0.319265C44.4698 -0.149975 44.3524 -0.126513 43.6953 0.553886Z"
      fill="currentColor"
    />
  </svg>
);

export function HeroBeforeAfterGallery({
  before,
  after,
  autoAdvanceMs = DEFAULT_AUTO_ADVANCE_MS,
  className,
}: HeroBeforeAfterGalleryProps) {
  const [activeIndex, setActiveIndex] = useState(0);

  const sanitizedAutoAdvance = Math.max(autoAdvanceMs, 2000);
  const afterFrames = useMemo(() => after.filter((frame) => Boolean(frame?.src)), [after]);

  useEffect(() => {
    if (afterFrames.length < 2) return;

    const reduceMotionQuery =
      typeof window !== "undefined" ? window.matchMedia("(prefers-reduced-motion: reduce)") : null;

    if (reduceMotionQuery?.matches) return;

    const interval = window.setInterval(() => {
      setActiveIndex((current) => (current + 1) % afterFrames.length);
    }, sanitizedAutoAdvance);

    return () => window.clearInterval(interval);
  }, [afterFrames.length, sanitizedAutoAdvance]);

  useEffect(() => {
    if (!afterFrames.length) {
      setActiveIndex(0);
    } else if (activeIndex > afterFrames.length - 1) {
      setActiveIndex(0);
    }
  }, [afterFrames.length, activeIndex]);

  const activeAfter = afterFrames[activeIndex] ?? afterFrames[0];

  return (
    <div className={cn("relative w-full", className)}>
      <div className="mt-20 flex flex-col gap-20 @lg:mt-0 @lg:flex-row @lg:items-start @lg:gap-8">
        <div className="relative @lg:flex-1">
          <div
            className="pointer-events-none absolute -top-10 right-0 flex flex-col items-end text-xs font-semibold tracking-[0.2em] text-orange-600 uppercase dark:text-orange-300"
            aria-hidden="true"
          >
            <span className="mb-1 inline-block [transform:rotate(12deg)]">Your missing preview</span>
            <HeroArrow className="mr-24 h-12 w-6 [transform:rotate(12deg)] text-orange-500" />
          </div>
          <FrameCard frame={before} label={before.badge ?? "Before"} />
        </div>

        <div className="relative @lg:flex-1">
          <div
            className="pointer-events-none absolute -top-10 left-24 flex flex-col items-center text-xs font-semibold tracking-[0.2em] text-orange-600 uppercase dark:text-orange-300"
            aria-hidden="true"
          >
            <span className="mb-1 inline-block [transform:rotate(-12deg)]">With OG Pilot</span>
            <HeroArrow className="ml-16 h-12 w-6 [transform:rotate(-12deg)_scaleX(-1)] text-orange-500" />
          </div>
          <AfterFrameCard frames={afterFrames} activeIndex={activeIndex} fallbackFrame={activeAfter} />
        </div>
      </div>

      {afterFrames.length > 1 ? (
        <div className="mt-6 flex items-center justify-center gap-2">
          {afterFrames.map((frame, index) => {
            const isActive = index === activeIndex;
            return (
              <button
                key={`${frame.src}-${index}`}
                type="button"
                onClick={() => setActiveIndex(index)}
                className={cn(
                  "h-2.5 w-2.5 rounded-full border border-orange-400/50 transition-all duration-300 focus-visible:ring-2 focus-visible:ring-orange-400/70 focus-visible:ring-offset-2 focus-visible:ring-offset-white focus-visible:outline-none dark:border-orange-300/40 dark:focus-visible:ring-offset-gray-950",
                  isActive
                    ? "bg-orange-500 shadow-[0_0_0_4px_rgba(249,115,22,0.18)] dark:bg-orange-400"
                    : "bg-orange-200/50 hover:bg-orange-300/70 dark:bg-orange-300/30 dark:hover:bg-orange-300/50",
                )}
                aria-label={`Show after example ${index + 1}`}
                aria-pressed={isActive}
              />
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

interface FrameCardProps {
  frame: GalleryFrame | undefined;
  label: string;
}

const FrameCard = ({ frame, label }: FrameCardProps) => {
  if (!frame) return null;

  return (
    <div className="relative flex flex-col gap-3">
      <span className="inline-flex w-fit items-center rounded-full bg-orange-100 px-3 py-1 text-xs font-semibold tracking-[0.27em] text-orange-600 uppercase dark:bg-orange-500/20 dark:text-orange-200">
        {label}
      </span>
      <div
        className={cn(
          "relative overflow-hidden rounded-2xl border border-orange-200/30 bg-black/5 dark:border-orange-500/30 dark:bg-white/5",
          "aspect-[1.9/1]",
        )}
      >
        <img src={frame.src} alt={frame.alt} loading="eager" className="h-full w-full object-cover" />
        <div className="pointer-events-none absolute inset-0 rounded-2xl ring-1 ring-black/10 dark:ring-white/10" />
      </div>
      {frame.caption ? (
        <p className="text-sm leading-relaxed text-gray-600 dark:text-gray-300">{frame.caption}</p>
      ) : null}
    </div>
  );
};

interface AfterFrameCardProps {
  frames: GalleryFrame[];
  activeIndex: number;
  fallbackFrame?: GalleryFrame;
}

const AfterFrameCard = ({ frames, activeIndex, fallbackFrame }: AfterFrameCardProps) => {
  const hasFrames = frames.length > 0;
  const normalizedFrames = hasFrames ? frames : fallbackFrame ? [fallbackFrame] : [];

  if (!normalizedFrames.length) {
    return null;
  }

  const safeIndex = Math.min(activeIndex, normalizedFrames.length - 1);

  return (
    <div className="relative flex flex-col gap-3">
      <span className="inline-flex w-fit items-center rounded-full bg-orange-500/10 px-3 py-1 text-xs font-semibold tracking-[0.27em] text-orange-600 uppercase dark:bg-orange-400/20 dark:text-orange-200">
        {normalizedFrames[safeIndex]?.badge ?? "After"}
      </span>
      <div className="relative aspect-[1.9/1] overflow-hidden rounded-2xl border border-orange-200/30 bg-black/5 dark:border-orange-500/30 dark:bg-white/5">
        {normalizedFrames.map((frame, index) => {
          const isActive = index === safeIndex;
          return (
            <img
              key={`${frame.src}-${index}`}
              src={frame.src}
              alt={frame.alt}
              loading={index === 0 ? "eager" : "lazy"}
              className={cn(
                "absolute inset-0 h-full w-full object-cover transition-opacity duration-700 ease-out",
                isActive ? "opacity-100" : "opacity-0",
              )}
              aria-hidden={!isActive}
            />
          );
        })}
        <div className="pointer-events-none absolute inset-0 rounded-2xl ring-1 ring-black/10 dark:ring-white/10" />
      </div>
      {normalizedFrames[safeIndex]?.caption ? (
        <p className="text-sm leading-relaxed text-gray-600 dark:text-gray-300">
          {normalizedFrames[safeIndex]?.caption}
        </p>
      ) : null}
    </div>
  );
};
